import React, { useState, useEffect, useCallback } from 'react';
import { permissionService } from '../api/services';
import { useSettings } from '../context/SettingsContext';
import AdminModal from './AdminModal';
import './AdminBountyPanel.css';

// 後台「懸賞與權限」分頁：懸賞系統設定（點數機制、退回開關）＋ 權限目錄管理（自由新增權限）＋ 角色 × 功能權限矩陣。
const AdminBountyPanel = () => {
  const { settings, updateSetting } = useSettings();
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [granted, setGranted] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingCell, setSavingCell] = useState('');
  const [settingBusy, setSettingBusy] = useState('');
  // 權限目錄管理用狀態
  const [showPermModal, setShowPermModal] = useState(false);
  const [editingPermKey, setEditingPermKey] = useState(null);
  const [permForm, setPermForm] = useState({ key: '', label: '', description: '' });
  const [permBusy, setPermBusy] = useState(false);

  const loadMatrix = useCallback(() => {
    setLoading(true);
    return permissionService.getMatrix()
      .then((res) => {
        setRoles(res.data?.roles || []);
        setCatalog(res.data?.catalog || []);
        setGranted(new Set((res.data?.granted || []).map((g) => `${g.role_code}|${g.permission_key}`)));
      })
      .catch((err) => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadMatrix(); }, [loadMatrix]);

  const cellKey = (role, perm) => `${role}|${perm}`;
  // ADMIN 為隱性全權，矩陣中一律顯示為已勾選且不可更動
  const isGranted = (role, perm) => role === 'ADMIN' || granted.has(cellKey(role, perm));

  const toggleCell = async (roleCode, permKey) => {
    if (roleCode === 'ADMIN') return;
    const k = cellKey(roleCode, permKey);
    const next = !granted.has(k);
    setSavingCell(k);
    setError('');
    // 樂觀更新，失敗時回滾
    setGranted((prev) => {
      const s = new Set(prev);
      if (next) s.add(k); else s.delete(k);
      return s;
    });
    try {
      await permissionService.setCell(roleCode, permKey, next);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setGranted((prev) => {
        const s = new Set(prev);
        if (next) s.delete(k); else s.add(k);
        return s;
      });
    } finally {
      setSavingCell('');
    }
  };

  const toggleSetting = async (settingKey) => {
    const current = settings[settingKey] === 'true';
    setSettingBusy(settingKey);
    setError('');
    try {
      await updateSetting(settingKey, current ? 'false' : 'true');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSettingBusy('');
    }
  };

  const openCreatePerm = () => { setEditingPermKey(null); setPermForm({ key: '', label: '', description: '' }); setShowPermModal(true); };
  const openEditPerm = (p) => { setEditingPermKey(p.key); setPermForm({ key: p.key, label: p.label, description: p.description || '' }); setShowPermModal(true); };

  const handleSavePerm = async () => {
    setPermBusy(true);
    setError('');
    try {
      if (editingPermKey) {
        await permissionService.updatePermission(editingPermKey, { label: permForm.label, description: permForm.description });
      } else {
        await permissionService.createPermission({ key: permForm.key.trim().toLowerCase(), label: permForm.label, description: permForm.description });
      }
      setShowPermModal(false);
      await loadMatrix();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setPermBusy(false);
    }
  };

  const handleDeletePerm = async (key) => {
    if (!window.confirm(`確定刪除權限「${key}」？已授予此權限的角色與個人指派將一併移除。`)) return;
    setError('');
    try {
      await permissionService.deletePermission(key);
      await loadMatrix();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const pointsOn = settings.bounty_points_enabled === 'true';
  const returnOn = settings.bounty_return_enabled === 'true';

  return (
    <div className="admin-bounty-panel">
      <h2>懸賞設定</h2>
      <div className="bounty-setting-list">
        <label className="bounty-setting-row">
          <div className="setting-text">
            <div className="setting-name">開立點數機制</div>
            <div className="setting-desc">開啟後，開立懸賞時須填寫點數；結案時點數計入負責人，並顯示於個人資料的累積點數。</div>
          </div>
          <input type="checkbox" className="bounty-switch" checked={pointsOn} disabled={settingBusy === 'bounty_points_enabled'} onChange={() => toggleSetting('bounty_points_enabled')} />
        </label>
        <label className="bounty-setting-row">
          <div className="setting-text">
            <div className="setting-name">領取後可退回</div>
            <div className="setting-desc">開啟後，負責人或具管理權限者可將已領取的懸賞退回為「開放中」（試營運後可視情況開關）。</div>
          </div>
          <input type="checkbox" className="bounty-switch" checked={returnOn} disabled={settingBusy === 'bounty_return_enabled'} onChange={() => toggleSetting('bounty_return_enabled')} />
        </label>
      </div>

      <div className="matrix-heading-row">
        <h2 className="matrix-heading">權限目錄</h2>
        <button className="btn btn-sm btn-primary" onClick={openCreatePerm}>＋ 新增權限</button>
      </div>
      <p className="matrix-hint">這裡定義系統中可被授予的功能權限。系統內建權限不可刪除，但可調整顯示名稱；自訂權限可自由新增與刪除。</p>
      {error && <div className="alert alert-danger">{error}</div>}
      {!loading && (
        <div className="perm-catalog-list">
          {catalog.map((p) => (
            <div key={p.key} className="perm-catalog-row">
              <div className="perm-catalog-text">
                <div className="perm-catalog-label">{p.label} <code>{p.key}</code>{p.is_system ? <span className="perm-catalog-badge">系統</span> : null}</div>
                {p.description && <div className="perm-catalog-desc">{p.description}</div>}
              </div>
              <div className="btn-group">
                <button className="btn btn-sm btn-secondary" onClick={() => openEditPerm(p)}>編輯</button>
                {!p.is_system && <button className="btn btn-sm btn-danger" onClick={() => handleDeletePerm(p.key)}>刪除</button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <h2 className="matrix-heading">角色權限矩陣</h2>
      <p className="matrix-hint">勾選每個角色可執行的功能。管理員（ADMIN）固定擁有全部權限，不可調整。個別使用者的額外權限可於「使用者管理」每列的「權限」按鈕設定。</p>
      {loading ? (
        <div className="matrix-loading">載入中...</div>
      ) : (
        <div className="matrix-wrap">
          <table className="admin-table matrix-table">
            <thead>
              <tr>
                <th>角色</th>
                {catalog.map((p) => <th key={p.key} title={p.description}>{p.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {roles.map((r) => (
                <tr key={r.code}>
                  <td className="matrix-role">{r.name} <code>{r.code}</code></td>
                  {catalog.map((p) => (
                    <td key={p.key} className="matrix-cell">
                      <input
                        type="checkbox"
                        checked={isGranted(r.code, p.key)}
                        disabled={r.code === 'ADMIN' || savingCell === cellKey(r.code, p.key)}
                        onChange={() => toggleCell(r.code, p.key)}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showPermModal && (
        <AdminModal title={editingPermKey ? '編輯權限' : '新增自訂權限'} onClose={() => setShowPermModal(false)}>
          <div className="admin-form-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 12 }}>
            <label>權限代碼（key）</label>
            <input
              className="form-control form-control-sm"
              placeholder="例如 report.export（小寫英文開頭，可含 . 與 _）"
              value={permForm.key}
              disabled={!!editingPermKey}
              onChange={(e) => setPermForm((p) => ({ ...p, key: e.target.value }))}
            />
            {editingPermKey && <div className="setting-desc">權限代碼建立後不可變更。</div>}
            <label>顯示名稱</label>
            <input className="form-control form-control-sm" placeholder="例如 匯出報表" value={permForm.label} onChange={(e) => setPermForm((p) => ({ ...p, label: e.target.value }))} />
            <label>說明</label>
            <input className="form-control form-control-sm" placeholder="補充說明（選填）" value={permForm.description} onChange={(e) => setPermForm((p) => ({ ...p, description: e.target.value }))} />
            <div className="btn-group" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-sm btn-primary" disabled={permBusy} onClick={handleSavePerm}>{permBusy ? '儲存中...' : '儲存'}</button>
              <button className="btn btn-sm btn-secondary" disabled={permBusy} onClick={() => setShowPermModal(false)}>取消</button>
            </div>
          </div>
        </AdminModal>
      )}
    </div>
  );
};

export default AdminBountyPanel;
